"use client";
import React, { useEffect, useState } from "react";
import {
  months,
  monthsEn,
  toNepaliNumber,
  useNepaliCalendar,
} from "../hooks/useNepaliCalendar";
import { type CalendarDay } from "../lib/api";
import { adToBS, getCurrentBSDate } from "../lib/dateConverter";
import "./NepaliCalendar.css";

const weekdays = ["आइत", "सोम", "मंगल", "बुध", "बिही", "शुक्र", "शनि"];
const weekdaysEn = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: string; // YYYY-MM-DD format (AD)
  end: string; // YYYY-MM-DD format (AD)
  color?: string;
  isHoliday?: boolean;
}

export interface NepaliCalendarProps {
  year?: number; // AD year (always AD format)
  month?: number; // AD month (always AD format, 1-12)
  bsYear?: number; // BS year (optional, takes precedence over year/month conversion if provided)
  bsMonth?: number; // BS month (optional, takes precedence over year/month conversion if provided)
  showToggle?: boolean;
  calendarMode?: "bs" | "ad";
  holidayWeekdays?: string | string[];
  events?: CalendarEvent[];
  showNationalHoliday?: boolean;
  eventVisibility?: "default" | "line";
  type?: "default" | "full";
  value?: string; // Date in format "YYYY-MM-DD" (always AD format)
  className?: string;
  calendarWrapperClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  navButtonClassName?: string;
  weekdaysClassName?: string;
  weekdayClassName?: string;
  calendarDaysClassName?: string;
  calendarDayClassName?: string;
  bsDateClassName?: string;
  adDateClassName?: string;
  toggleWrapperClassName?: string;
  toggleButtonClassName?: string;
  styles?: any;
  prevArrow?: string | React.ReactNode;
  nextArrow?: string | React.ReactNode;
  weekdayLabels?: string[];
  isClickable?: boolean;
  onDateSelect?: (date: CalendarDay) => void;
  onMonthChange?: (year: number, month: number) => void;
  onCalendarModeChange?: (mode: "bs" | "ad") => void;
  onEventClick?: (event: CalendarEvent) => void;
  showNationalHolidayToggle?: boolean;
  onNationalHolidayToggleChange?: (show: boolean) => void;
  showAllNationalHolidays?: boolean;
  tillCurrentYear?: boolean; // If true, show years only up to current year (for birthday selection). Default: false (shows future years too for planning)
  showArrows?: boolean; // Show/hide navigation arrows (default: true)
  useLaneBasedEventPositioning?: boolean; // If true, use new lane-based event positioning (default: true). Set to false to use old algorithm
}

interface EventPosition {
  event: CalendarEvent;
  row: number;
  startCol: number;
  span: number;
}

const NepaliCalendar: React.FC<NepaliCalendarProps> = ({
  year: initialYear,
  month: initialMonth,
  bsYear: initialBSYear,
  bsMonth: initialBSMonth,
  showToggle = false,
  calendarMode: initialCalendarMode = "bs",
  holidayWeekdays = "Saturday",
  events = [],
  showNationalHoliday: initialShowNationalHoliday = true,
  eventVisibility = "default",
  type = "full",
  value,
  className = "",
  calendarWrapperClassName = "",
  headerClassName = "",
  titleClassName = "",
  navButtonClassName = "",
  weekdaysClassName = "",
  weekdayClassName = "",
  calendarDaysClassName = "",
  calendarDayClassName = "",
  bsDateClassName = "",
  adDateClassName = "",
  toggleWrapperClassName = "",
  toggleButtonClassName = "",
  styles = {},
  prevArrow = "←",
  nextArrow = "→",
  weekdayLabels,
  isClickable = true,
  onDateSelect,
  onMonthChange,
  onCalendarModeChange,
  onEventClick,
  showNationalHolidayToggle = false,
  onNationalHolidayToggleChange,
  showAllNationalHolidays = false,
  tillCurrentYear = false,
  showArrows = true,
  useLaneBasedEventPositioning = true, // Default to new algorithm
}) => {
  const [showMoreModal, setShowMoreModal] = useState<{
    date: string;
    events: CalendarEvent[];
    position: { x: number; y: number };
  } | null>(null);
  const [hoveredEvent, setHoveredEvent] = useState<{
    event: CalendarEvent;
    position: { x: number; y: number };
  } | null>(null);
  const [hoveredDay, setHoveredDay] = useState<{
    date: string;
    events: CalendarEvent[];
    position: { x: number; y: number };
  } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showNationalHoliday, setShowNationalHoliday] = useState(
    initialShowNationalHoliday
  );
  const [adModeDays, setAdModeDays] = useState<CalendarDay[] | null>(null);

  // Compute initial year/month from value if provided
  // Priority: bsYear/bsMonth > value prop > year/month props
  // Value is always in AD format - extract AD year/month for hook conversion
  const getInitialYearFromValue = (): number | undefined => {
    // If bsYear/bsMonth are provided, value prop is ignored (hook handles bsYear/bsMonth directly)
    if (initialBSYear !== undefined && initialBSYear !== null &&
        initialBSMonth !== undefined && initialBSMonth !== null) {
      return initialYear; // Return original year prop (may be undefined, that's ok)
    }
    
    // Extract AD year from value prop if provided
    if (value) {
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10);
        const day = parseInt(dateParts[2], 10);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          // Value is always in AD format, return AD year for hook to convert
          return year;
        }
      }
    }
    
    // Fallback to original year prop (AD format)
    return initialYear;
  };

  const getInitialMonthFromValue = (): number | undefined => {
    // If bsYear/bsMonth are provided, value prop is ignored (hook handles bsYear/bsMonth directly)
    if (initialBSYear !== undefined && initialBSYear !== null &&
        initialBSMonth !== undefined && initialBSMonth !== null) {
      return initialMonth; // Return original month prop (may be undefined, that's ok)
    }
    
    // Extract AD month from value prop if provided
    if (value) {
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10);
        const day = parseInt(dateParts[2], 10);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          // Value is always in AD format, return AD month for hook to convert
          return month;
        }
      }
    }
    
    // Fallback to original month prop (AD format)
    return initialMonth;
  };

  // Sync internal state with prop if it changes externally
  useEffect(() => {
    setShowNationalHoliday(initialShowNationalHoliday);
  }, [initialShowNationalHoliday]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 500);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const {
    currentBSYear,
    currentBSMonth,
    calendarData,
    isLoading,
    isYearLoading,
    isMonthLoading,
    calendarMode,
    isIndexedDBInitialized,
    showMonthDropdown,
    showYearDropdown,
    selectedBSKey,
    selectedADKey,
    titleRef,
    setShowMonthDropdown,
    setShowYearDropdown,
    setSelectedBSKey,
    setSelectedADKey,
    getDataForKey,
    getADMonthRange,
    getDisplayMonthYear,
    getDropdownDisplayValues,
    primaryADMonth,
    primaryADYear,
    handleDayClick,
    handlePrevMonth,
    handleNextMonth,
    handleCalendarModeChange,
    handleMonthSelect,
    handleYearSelect,
    fetchMonthData,
    fetchAllBSMonthsForADMonth,
  } = useNepaliCalendar({
    year: getInitialYearFromValue(),
    month: getInitialMonthFromValue(),
    bsYear: initialBSYear,
    bsMonth: initialBSMonth,
    calendarMode: initialCalendarMode,
    value,
    onDateSelect,
    onMonthChange,
    onCalendarModeChange,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (titleRef.current && !titleRef.current.contains(target)) {
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [titleRef, setShowMonthDropdown, setShowYearDropdown]);

  // Fetch complete AD month days when in AD mode
  useEffect(() => {
    if (
      calendarMode === "ad" &&
      isIndexedDBInitialized &&
      fetchAllBSMonthsForADMonth
    ) {
      // Use primaryADMonth/primaryADYear if available (for navigation), otherwise use getDisplayMonthYear
      let adYear: number;
      let adMonth: number;

      if (primaryADMonth !== null && primaryADYear !== null) {
        adYear = primaryADYear;
        adMonth = primaryADMonth;
      } else {
        const displayInfo = getDisplayMonthYear();
        adYear = displayInfo.yearNum;
        adMonth = displayInfo.monthNum;
      }

      // Only fetch if we have valid AD year/month (within supported range)
      if (adYear >= 1921 && adYear <= 2032 && adMonth >= 1 && adMonth <= 12) {
        fetchAllBSMonthsForADMonth(adYear, adMonth).then((days) => {
          setAdModeDays(days);
        });
      } else {
        setAdModeDays(null);
      }
    } else {
      setAdModeDays(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    calendarMode,
    primaryADMonth,
    primaryADYear,
    isIndexedDBInitialized,
    fetchAllBSMonthsForADMonth,
    // Note: getDisplayMonthYear is intentionally excluded from deps to avoid infinite loops
    // currentBSYear/currentBSMonth are excluded because we use primaryADMonth/primaryADYear for AD mode
  ]);

  const isDateInEvent = (date: string, event: CalendarEvent): boolean => {
    return date >= event.start && date <= event.end;
  };

  // Convert day.events to CalendarEvent format
  const convertDayEventToCalendarEvent = (
    dayEvent: NonNullable<CalendarDay["events"]>[0],
    date: string
  ): CalendarEvent => {
    return {
      id: `day-event-${dayEvent.id}-${date}`, // Unique ID to avoid conflicts
      title: dayEvent.title || dayEvent.title_en || "",
      description: dayEvent.description || undefined,
      start: date,
      end: date,
      color: dayEvent.national_holiday === 1 ? "#ef4444" : "#3b82f6", // Red for national holidays, blue for others
      isHoliday: dayEvent.national_holiday === 1,
    };
  };

  // Get all events for a date (from props and from day.events)
  // Filter out day.events where national_holiday === 0, but keep all prop events
  // Respect showNationalHoliday prop - only include national holidays if it's true
  // If showAllNationalHolidays is true, also include events with national_holiday === 0 when showNationalHoliday is true
  const getAllEventsForDate = (
    date: string,
    dayEvents?: CalendarDay["events"]
  ): CalendarEvent[] => {
    const propEvents = events.filter((event) => isDateInEvent(date, event));
    const apiEvents: CalendarEvent[] = [];

    if (showNationalHoliday && dayEvents) {
      // Include events with national_holiday === 1
      const nationalHolidays = dayEvents
        .filter((event) => event.national_holiday === 1)
        .map((event) => convertDayEventToCalendarEvent(event, date));
      apiEvents.push(...nationalHolidays);

      // If showAllNationalHolidays is true, also include events with national_holiday === 0
      if (showAllNationalHolidays) {
        const otherEvents = dayEvents
          .filter((event) => event.national_holiday === 0)
          .map((event) => convertDayEventToCalendarEvent(event, date));
        apiEvents.push(...otherEvents);
      }
    }

    return [...propEvents, ...apiEvents];
  };

  // ============================================================================
  // OLD ALGORITHM (BACKUP) - Row-based event positioning
  // ============================================================================
  // This is the original algorithm. Kept as backup in case we need to revert.
  // To use: set useLaneBasedEventPositioning={false}
  const calculateEventPositionsOld = (
    days: CalendarDay[],
    startingWeekday: number
  ): EventPosition[] => {
    const positions: EventPosition[] = [];
    const rowsUsed: Map<number, Map<number, number>> = new Map();

    // Collect all events (from props and from day.events)
    // Prioritize prop events first, then national holidays from day.events
    const allEvents: CalendarEvent[] = [];

    // Add prop events first - these should be prioritized
    // When type === "default", skip user events (only show national holidays)
    if (type === "full") {
      allEvents.push(...events);
    }

    // Add day events (national holidays) after prop events
    // Only include national holidays if showNationalHoliday is true
    // If showAllNationalHolidays is true, also include events with national_holiday === 0
    // When type === "default", don't add national holidays to allEvents (only highlight dates)
    if (showNationalHoliday && type === "full") {
      days.forEach((day) => {
        if (day.events && day.events.length > 0) {
          // Add events with national_holiday === 1
          day.events
            .filter((dayEvent) => dayEvent.national_holiday === 1)
            .forEach((dayEvent) => {
              allEvents.push(
                convertDayEventToCalendarEvent(dayEvent, day.ad.date)
              );
            });

          // If showAllNationalHolidays is true, also add events with national_holiday === 0
          if (showAllNationalHolidays) {
            day.events
              .filter((dayEvent) => dayEvent.national_holiday === 0)
              .forEach((dayEvent) => {
                allEvents.push(
                  convertDayEventToCalendarEvent(dayEvent, day.ad.date)
                );
              });
          }
        }
      });
    }

    allEvents.forEach((event) => {
      const eventDays = days.filter((day) => isDateInEvent(day.ad.date, event));

      if (eventDays.length === 0) return;

      // Find startIndex - prefer exact match first
      let startIndex = days.findIndex((day) => day.ad.date === event.start);
      if (startIndex === -1) {
        startIndex = days.findIndex((day) => day.ad.date >= event.start);
      }
      if (startIndex === -1) startIndex = 0;

      // Find endIndex - prefer exact match first
      let endIndex = days.findIndex((day) => day.ad.date === event.end);
      if (endIndex === -1) {
        const lastEventDay = days.findIndex((day) => day.ad.date > event.end);
        endIndex = lastEventDay === -1 ? days.length - 1 : lastEventDay - 1;
      }

      // Ensure indices are valid
      if (startIndex < 0 || startIndex >= days.length) return;
      if (endIndex < 0 || endIndex >= days.length) return;
      if (startIndex > endIndex) return;

      const startCol = (startingWeekday + startIndex) % 7;
      const endCol = (startingWeekday + endIndex) % 7;
      const startRow = Math.floor((startingWeekday + startIndex) / 7);
      const endRow = Math.floor((startingWeekday + endIndex) / 7);

      for (let row = startRow; row <= endRow; row++) {
        const isFirstRow = row === startRow;
        const isLastRow = row === endRow;

        const colStart = isFirstRow ? startCol : 0;
        const colEnd = isLastRow ? endCol : 6;
        const span = colEnd - colStart + 1;

        if (!rowsUsed.has(row)) {
          rowsUsed.set(row, new Map());
        }

        const rowData = rowsUsed.get(row)!;
        let eventRow = 0;

        while (eventRow < 2) {
          const maxCol = rowData.get(eventRow) ?? -1;
          if (maxCol < colStart) {
            break;
          }
          eventRow++;
        }

        if (eventRow >= 2) continue;

        rowData.set(eventRow, colEnd);

        positions.push({
          event,
          row: row * 100 + eventRow,
          startCol: colStart,
          span,
        });
      }
    });

    return positions;
  };

  // ============================================================================
  // NEW ALGORITHM - Lane-based event positioning (FullCalendar-style)
  // ============================================================================
  // This algorithm uses global lane assignment to ensure:
  // 1. Multi-day events stay continuous (same lane across all dates)
  // 2. Single events on dates with fewer events are visible
  // 3. Better distribution of events across lanes
  const calculateEventPositionsLaneBased = (
    days: CalendarDay[],
    startingWeekday: number
  ): EventPosition[] => {
    const positions: EventPosition[] = [];
    const MAX_VISIBLE_LANES = 2; // Maximum number of visible event lanes per day

    // Collect all events (from props and from day.events)
    // Prioritize prop events first, then national holidays from day.events
    const allEvents: CalendarEvent[] = [];

    // Add prop events first - these should be prioritized
    // When type === "default", skip user events (only show national holidays)
    if (type === "full") {
      allEvents.push(...events);
    }

    // Add day events (national holidays) after prop events
    // Only include national holidays if showNationalHoliday is true
    // If showAllNationalHolidays is true, also include events with national_holiday === 0
    // When type === "default", don't add national holidays to allEvents (only highlight dates)
    if (showNationalHoliday && type === "full") {
      days.forEach((day) => {
        if (day.events && day.events.length > 0) {
          // Add events with national_holiday === 1
          day.events
            .filter((dayEvent) => dayEvent.national_holiday === 1)
            .forEach((dayEvent) => {
              allEvents.push(
                convertDayEventToCalendarEvent(dayEvent, day.ad.date)
              );
            });

          // If showAllNationalHolidays is true, also add events with national_holiday === 0
          if (showAllNationalHolidays) {
            day.events
              .filter((dayEvent) => dayEvent.national_holiday === 0)
              .forEach((dayEvent) => {
                allEvents.push(
                  convertDayEventToCalendarEvent(dayEvent, day.ad.date)
                );
              });
          }
        }
      });
    }

    if (allEvents.length === 0) return positions;

    // Step 1: Sort events by priority
    // Priority: Multi-day events first (longest first), then single-day events (by date)
    const sortedEvents = [...allEvents].sort((a, b) => {
      const aStart = new Date(a.start).getTime();
      const aEnd = new Date(a.end).getTime();
      const bStart = new Date(b.start).getTime();
      const bEnd = new Date(b.end).getTime();
      
      const aDuration = aEnd - aStart;
      const bDuration = bEnd - bStart;
      const aIsMultiDay = aDuration > 0;
      const bIsMultiDay = bDuration > 0;

      // Multi-day events come first
      if (aIsMultiDay && !bIsMultiDay) return -1;
      if (!aIsMultiDay && bIsMultiDay) return 1;
      
      // If both are multi-day, longer duration first
      if (aIsMultiDay && bIsMultiDay) {
        if (bDuration !== aDuration) return bDuration - aDuration;
      }
      
      // Then sort by start date
      if (aStart !== bStart) return aStart - bStart;
      
      // Finally by end date
      return aEnd - bEnd;
    });

    // Step 2: Track lane occupancy for each date
    // Key: date string (YYYY-MM-DD), Value: Set of lane numbers occupied on that date
    const laneOccupancy: Map<string, Set<number>> = new Map();

    // Helper: Get all dates an event spans (within the visible calendar days)
    const getEventDates = (event: CalendarEvent): string[] => {
      const dates: string[] = [];
      
      // Find which calendar days this event spans
      days.forEach((day) => {
        if (isDateInEvent(day.ad.date, event)) {
          dates.push(day.ad.date);
        }
      });
      
      return dates;
    };

    // Helper: Check if a lane is free for all dates an event spans
    const isLaneFree = (lane: number, dates: string[]): boolean => {
      for (const date of dates) {
        const occupiedLanes = laneOccupancy.get(date);
        if (occupiedLanes && occupiedLanes.has(lane)) {
          return false;
        }
      }
      return true;
    };

    // Helper: Assign event to a lane and mark occupancy
    const assignLane = (lane: number, dates: string[]): void => {
      for (const date of dates) {
        if (!laneOccupancy.has(date)) {
          laneOccupancy.set(date, new Set());
        }
        laneOccupancy.get(date)!.add(lane);
      }
    };

    // Step 3: Assign lanes to events
    interface LaneAssignment {
      event: CalendarEvent;
      lane: number;
      dates: string[];
    }

    const laneAssignments: LaneAssignment[] = [];

    for (const event of sortedEvents) {
      const eventDates = getEventDates(event);
      if (eventDates.length === 0) continue;

      // Find the first available lane
      let assignedLane = -1;
      for (let lane = 0; lane < MAX_VISIBLE_LANES; lane++) {
        if (isLaneFree(lane, eventDates)) {
          assignedLane = lane;
          assignLane(lane, eventDates);
          break;
        }
      }

      // If no lane available, event goes to +N button (don't assign)
      if (assignedLane >= 0) {
        laneAssignments.push({
          event,
          lane: assignedLane,
          dates: eventDates,
        });
      }
    }

    // Step 4: Convert lane assignments to EventPosition format
    // EventPosition uses: row (weekRow * 100 + eventRow), startCol, span
    for (const assignment of laneAssignments) {
      const { event, lane, dates } = assignment;
      
      if (dates.length === 0) continue;

      // Find start and end indices in days array
      let startIndex = days.findIndex((day) => day.ad.date === dates[0]);
      let endIndex = days.findIndex((day) => day.ad.date === dates[dates.length - 1]);

      if (startIndex === -1) {
        startIndex = days.findIndex((day) => day.ad.date >= dates[0]);
      }
      if (startIndex === -1) startIndex = 0;

      if (endIndex === -1) {
        const lastEventDay = days.findIndex((day) => day.ad.date > dates[dates.length - 1]);
        endIndex = lastEventDay === -1 ? days.length - 1 : lastEventDay - 1;
      }

      // Ensure indices are valid
      if (startIndex < 0 || startIndex >= days.length) continue;
      if (endIndex < 0 || endIndex >= days.length) continue;
      if (startIndex > endIndex) continue;

      const startCol = (startingWeekday + startIndex) % 7;
      const endCol = (startingWeekday + endIndex) % 7;
      const startRow = Math.floor((startingWeekday + startIndex) / 7);
      const endRow = Math.floor((startingWeekday + endIndex) / 7);

      // Create position for each week row the event spans
      for (let row = startRow; row <= endRow; row++) {
        const isFirstRow = row === startRow;
        const isLastRow = row === endRow;

        const colStart = isFirstRow ? startCol : 0;
        const colEnd = isLastRow ? endCol : 6;
        const span = colEnd - colStart + 1;

        positions.push({
          event,
          row: row * 100 + lane, // Use lane as eventRow
          startCol: colStart,
          span,
        });
      }
    }

    return positions;
  };

  // ============================================================================
  // MAIN FUNCTION - Selects algorithm based on prop
  // ============================================================================
  const calculateEventPositions = (
    days: CalendarDay[],
    startingWeekday: number
  ): EventPosition[] => {
    if (useLaneBasedEventPositioning) {
      return calculateEventPositionsLaneBased(days, startingWeekday);
    } else {
      return calculateEventPositionsOld(days, startingWeekday);
    }
  };

  const renderCalendarDays = () => {
    if (isLoading || isYearLoading || !isIndexedDBInitialized) {
      return (
        <div
          className="nepali-calendar-loading-spinner-container"
          style={styles.loadingSpinner}
        >
          <div className="nepali-calendar-loading-spinner" />
          <div
            className="nepali-calendar-loading-text"
            style={styles.loadingText}
          >
            क्यालेन्डर डाटा लोड हुँदैछ...
          </div>
        </div>
      );
    }

    if (isMonthLoading) {
      return (
        <div
          className="nepali-calendar-loading-spinner-container"
          style={styles.loadingSpinner}
        >
          <div className="nepali-calendar-loading-spinner" />
          <div
            className="nepali-calendar-loading-text"
            style={styles.loadingText}
          >
            क्यालेन्डर डाटा लोड हुँदैछ...
          </div>
        </div>
      );
    }

    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData) return null;

    let { days, otherData } = currentData;
    let startingWeekday = parseInt(otherData.starting_weekday.number);

    // Verify and correct "today" flag in BS mode
    if (calendarMode === "bs") {
      const currentBS = getCurrentBSDate();
      if (currentBS) {
        // Check if we're displaying the current month
        const isCurrentMonth =
          currentBS.bs_y === currentBSYear && currentBS.bs_m === currentBSMonth;

        if (isCurrentMonth) {
          // Verify today flag is on the correct day, or add it if missing
          const todayBSDate = `${currentBS.bs_y}-${String(
            currentBS.bs_m
          ).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;

          // Update days array: remove today flag from all, then set it on the correct day
          days = days.map((day) => {
            const isToday = day.bs.date === todayBSDate;
            return { ...day, today: isToday };
          });
        }
      }
    }

    // In AD mode, use the complete AD month days fetched from all BS months
    let adModeFirstDay = 1; // Track the first day number we have in AD mode
    if (calendarMode === "ad" && adModeDays && adModeDays.length > 0) {
      days = adModeDays;

      // Verify and correct "today" flag in AD mode
      const currentBS = getCurrentBSDate();
      if (currentBS) {
        const todayBSDate = `${currentBS.bs_y}-${String(
          currentBS.bs_m
        ).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;
        // Update days array: remove today flag from all, then set it on the correct day
        days = days.map((day) => {
          const isToday = day.bs.date === todayBSDate;
          return { ...day, today: isToday };
        });
      }

      // Find day 1, or use the first available day
      const firstDay = days.find((day) => day.ad && day.ad.ad_d === 1);
      if (firstDay && firstDay.ad) {
        adModeFirstDay = 1;
      } else {
        const firstAvailableDay = days[0];
        if (firstAvailableDay && firstAvailableDay.ad) {
          adModeFirstDay = firstAvailableDay.ad.ad_d;
        }
      }

      // Calculate starting weekday from day 1 of the AD month
      const displayInfo = getDisplayMonthYear();
      const adYear = displayInfo.yearNum;
      const adMonth = displayInfo.monthNum;
      const firstDayDate = new Date(adYear, adMonth - 1, 1);
      startingWeekday = firstDayDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    } else if (calendarMode === "ad") {
      // Fallback: filter days from current data if adModeDays not loaded yet
      const displayInfo = getDisplayMonthYear();
      const adYear = displayInfo.yearNum;
      const adMonth = displayInfo.monthNum;

      const filteredDays = days.filter(
        (day) => day.ad && day.ad.ad_y === adYear && day.ad.ad_m === adMonth
      );

      if (filteredDays.length > 0) {
        days = filteredDays;
        const firstAvailableDay = filteredDays.reduce((prev, curr) => {
          if (!prev || !prev.ad) return curr;
          if (!curr || !curr.ad) return prev;
          return prev.ad.ad_d < curr.ad.ad_d ? prev : curr;
        });

        if (firstAvailableDay && firstAvailableDay.ad) {
          adModeFirstDay = firstAvailableDay.ad.ad_d;
          const firstDayDate = new Date(adYear, adMonth - 1, 1);
          startingWeekday = firstDayDate.getDay();
        }
      }
    }

    const daysArray: React.ReactNode[] = [];

    // Calculate total empty cells needed before the first day
    // In AD mode: startingWeekday (for day 1 alignment) + (adModeFirstDay - 1) if day 1 is missing
    // In BS mode: just startingWeekday
    const totalEmptyCells =
      calendarMode === "ad" && adModeFirstDay > 1
        ? startingWeekday + (adModeFirstDay - 1)
        : startingWeekday;

    for (let i = 0; i < totalEmptyCells; i++) {
      daysArray.push(
        <div key={`empty-${i}`} className="nepali-calendar-day empty" />
      );
    }

    const normalizeHolidaySet = (input: string | string[]): Set<number> => {
      const toArray = Array.isArray(input) ? input : [input];
      const map: Record<string, number> = {
        sunday: 0,
        sun: 0,
        monday: 1,
        mon: 1,
        tuesday: 2,
        tue: 2,
        wednesday: 3,
        wed: 3,
        thursday: 4,
        thu: 4,
        friday: 5,
        fri: 5,
        saturday: 6,
        sat: 6,
      };
      const set = new Set<number>();
      for (const item of toArray) {
        const key = String(item).trim().toLowerCase();
        if (map[key] !== undefined) set.add(map[key]);
      }
      return set;
    };

    const holidayIndexSet = normalizeHolidaySet(holidayWeekdays);
    // Use the filtered days array for event positions calculation
    const eventPositions = calculateEventPositions(days, startingWeekday);

    const eventsPerDay = new Map<number, number>();
    eventPositions.forEach((pos) => {
      for (let col = pos.startCol; col < pos.startCol + pos.span; col++) {
        const weekRow = Math.floor(pos.row / 100);
        const dayIndex = weekRow * 7 + col - startingWeekday;
        if (dayIndex >= 0 && dayIndex < days.length) {
          eventsPerDay.set(dayIndex, (eventsPerDay.get(dayIndex) || 0) + 1);
        }
      }
    });

    days.forEach((day, index) => {
      const weekdayIndex = (startingWeekday + index) % 7;
      const isHolidayWeekday = holidayIndexSet.has(weekdayIndex);

      // Check if day has national holiday
      // When type === "default", always check for national holidays (regardless of showNationalHoliday prop)
      // because type="default" is meant to show national holidays highlighted
      const isNationalHoliday =
        (type === "default" || showNationalHoliday) &&
        day.events?.some((event) => event.national_holiday === 1);

      // When type === "default", don't apply holiday class (no background) but still mark as national holiday for text color
      const isHoliday =
        isHolidayWeekday || (isNationalHoliday && type !== "default");

      const isSelected =
        calendarMode === "bs"
          ? selectedBSKey === day.bs.date
          : selectedADKey === day.ad.date;

      const dayEventsAll = getAllEventsForDate(day.ad.date, day.events);
      const visibleEventsCount = eventsPerDay.get(index) || 0;
      // When type === "default", don't show "show more" button
      const hasMoreEvents =
        type === "full" && dayEventsAll.length > visibleEventsCount;

      // Get national holidays for this day (for hover popup when type === "default")
      // When type === "default", always get national holidays regardless of showNationalHoliday
      const nationalHolidays =
        type === "default" && day.events
          ? day.events
              .filter((event) => event.national_holiday === 1)
              .map((event) =>
                convertDayEventToCalendarEvent(event, day.ad.date)
              )
          : [];

      daysArray.push(
        <div
          key={index}
          className={`nepali-calendar-day ${calendarDayClassName} ${
            isHoliday ? "holiday" : ""
          } ${day.today ? "today" : ""} ${isSelected ? "selected" : ""}`}
          data-national-holiday={
            type === "default" && isNationalHoliday ? "true" : undefined
          }
          onClick={(e) => {
            if (!isClickable) return;
            const target = e.target as HTMLElement;
            if (
              target.closest(".calendar-event-bar") ||
              target.closest(".show-more-button")
            ) {
              return;
            }
            handleDayClick(day);
          }}
          style={{
            ...styles.calendarDay,
            ...(isHoliday ? styles.holiday ?? styles.saturday : {}),
            position: "relative",
            padding: "4px",
            cursor: isClickable ? "pointer" : "default",
          }}
          onMouseEnter={(e) => {
            // When type === "default" and there are national holidays, show popup
            if (
              type === "default" &&
              nationalHolidays.length > 0 &&
              !isMobile
            ) {
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredDay({
                date: day.ad.date,
                events: nationalHolidays,
                position: { x: rect.left + rect.width / 2, y: rect.top },
              });
            }
          }}
          onMouseLeave={() => {
            if (type === "default") {
              setHoveredDay(null);
            }
          }}
        >
          {calendarMode === "bs" ? (
            <>
              <div
                className={`bs-date ${bsDateClassName}`}
                style={{
                  position: "absolute",
                  top: "1px",
                  left: day.today ? "20px" : "4px",
                  fontSize: isMobile ? "0.9rem" : "1.1rem",
                  fontWeight: 500,
                  lineHeight: 1,
                  zIndex: 3,
                  ...styles.bsDate,
                  color:
                    day.today || isSelected
                      ? "blue"
                      : isNationalHoliday && type === "default"
                      ? "#ef4444"
                      : styles.bsDate?.color || "#1f2937",
                }}
              >
                {day.bs?.date_np || ""}
              </div>
              <div
                className={`ad-date ${adDateClassName}`}
                style={{
                  position: "absolute",
                  right: "4px",
                  bottom: "4px",
                  fontSize: isMobile ? "9px" : "11px",
                  color:
                    isNationalHoliday && type === "default"
                      ? "#ef4444"
                      : "#111827",
                  opacity: day.today || isSelected ? 0.8 : 0.6,
                  fontWeight: 400,
                  lineHeight: 1,
                  zIndex: 3,
                  ...styles.adDate,
                }}
              >
                {day.ad?.ad_d ??
                  (day.ad?.date ? day.ad.date.split("-")[2] : "")}
              </div>
            </>
          ) : (
            <div
              className={`ad-date ${adDateClassName}`}
              style={{
                position: "absolute",
                top: "1px",
                left: day.today ? "20px" : "4px",
                fontSize: isMobile ? "0.9rem" : "1.1rem",
                fontWeight: 500,
                lineHeight: 1,
                zIndex: 3,
                ...styles.adDate,
                color:
                  day.today || isSelected
                    ? "blue"
                    : isNationalHoliday && type === "default"
                    ? "#ef4444"
                    : styles.adDate?.color || "#1f2937",
              }}
            >
              {day.ad?.ad_d ?? (day.ad?.date ? day.ad.date.split("-")[2] : "")}
            </div>
          )}

          {hasMoreEvents && (
            <button
              className="show-more-button"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setShowMoreModal({
                  date: day.ad.date,
                  events: dayEventsAll,
                  position: { x: rect.left, y: rect.bottom },
                });
              }}
              style={{
                position: "absolute",
                bottom: "2px",
                left: "10px",
                transform: "translateX(-50%)",
                fontSize: "8px",
                padding: isMobile ? "1px 3px" : "1px 4px",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                color: "white",
                border: "none",
                borderRadius: "2px",
                cursor: "pointer",
                zIndex: 3,
                fontWeight: 500,
              }}
            >
              +{dayEventsAll.length - visibleEventsCount}
            </button>
          )}

          {/* Show national holiday indicator if applicable */}
          {/* When type === "default", don't show the red dot */}
          {isNationalHoliday && type !== "default" && (
            <div
              style={{
                position: "absolute",
                top: "2px",
                right: "4px",
                width: isMobile ? "6px" : "8px",
                height: isMobile ? "6px" : "8px",
                backgroundColor: "#ef4444",
                borderRadius: "50%",
                zIndex: 3,
              }}
              title="National Holiday"
            />
          )}
        </div>
      );
    });

    const remainingCells = 42 - daysArray.length;
    for (let i = 0; i < remainingCells; i++) {
      daysArray.push(
        <div key={`empty-end-${i}`} className="nepali-calendar-day empty" />
      );
    }

    return (
      <>
        {daysArray}
        {/* Render continuous event bars */}
        {eventPositions.map((pos, idx) => {
          const weekRow = Math.floor(pos.row / 100);
          const eventRow = pos.row % 100;

          return (
            <div
              key={`${pos.event.id}-${idx}`}
              className="calendar-event-bar"
              onClick={(e) => {
                e.stopPropagation();
                onEventClick?.(pos.event);
              }}
              onMouseEnter={(e) => {
                // Show tooltip for all events (with or without description)
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredEvent({
                  event: pos.event,
                  position: { x: rect.left + rect.width / 2, y: rect.top },
                });
              }}
              onMouseLeave={() => setHoveredEvent(null)}
              style={{
                position: "absolute",
                left: `calc(${(pos.startCol / 7) * 100}% + 4px)`,
                top: `calc(${weekRow * (100 / 6)}% + ${
                  eventRow * (isMobile ? 16 : 20) + (isMobile ? 24 : 28)
                }px)`,
                width: `calc(${(pos.span / 7) * 100}% - 8px)`,
                height:
                  eventVisibility === "line"
                    ? isMobile
                      ? "2px"
                      : "3px"
                    : isMobile
                    ? "14px"
                    : "17px",
                backgroundColor: pos.event.color || "#3b82f6",
                color: eventVisibility === "line" ? "transparent" : "white",
                fontSize: isMobile ? "9px" : "11px",
                padding:
                  eventVisibility === "line"
                    ? "0"
                    : isMobile
                    ? "1px 4px"
                    : "2px 6px",
                borderRadius: eventVisibility === "line" ? "0" : "4px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                fontWeight: 500,
                boxSizing: "border-box",
              }}
            >
              {eventVisibility === "line" ? null : pos.event.title}
            </div>
          );
        })}
      </>
    );
  };

  const renderMonths = () => {
    const monthList = calendarMode === "bs" ? months : monthsEn;
    const { selectedMonth } = getDropdownDisplayValues();
    return monthList.map((month, index) => (
      <div
        key={index}
        className="nepali-calendar-dropdown-item"
        onClick={() => handleMonthSelect(index + 1)}
        style={{
          padding: "8px",
          cursor: "pointer",
          backgroundColor: selectedMonth === index + 1 ? "#e8f4f8" : "white",
        }}
      >
        {month}
      </div>
    ));
  };

  const renderYears = () => {
    const years: number[] = [];
    const { selectedYear } = getDropdownDisplayValues();
    if (calendarMode === "bs") {
      // BS mode: 2033 to 2089 (or up to current BS year if tillCurrentYear is true)
      const currentBSYear = getCurrentBSDate()?.bs_y || 2081;
      const endYear = tillCurrentYear ? currentBSYear : 2089;
      for (let i = 2033; i <= endYear; i++) {
        years.push(i);
      }
    } else {
      // AD mode: 1943 to 2033 (or up to current year if tillCurrentYear is true)
      const currentADYear = new Date().getFullYear();
      const endYear = tillCurrentYear ? currentADYear : 2033;
      for (let i = 1943; i <= endYear; i++) {
        years.push(i);
      }
    }
    return years.map((y) => (
      <div
        key={y}
        className="nepali-calendar-dropdown-item"
        onClick={() => handleYearSelect(y)}
        style={{
          padding: "8px",
          cursor: "pointer",
          backgroundColor: selectedYear === y ? "#e8f4f8" : "white",
        }}
      >
        {calendarMode === "bs" ? toNepaliNumber(y) : y}
      </div>
    ));
  };

  const weekdaysToDisplay =
    weekdayLabels || (calendarMode === "bs" ? weekdays : weekdaysEn);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <style>{`
        .nepali-calendar-day {
          min-height: ${isMobile ? "90px" : "90px"} !important;
          height: auto !important;
          aspect-ratio: auto !important;
        }

        .nepali-calendar-day.today {
          ${
            value
              ? `
              background-color: initial !important;
              border: none !important;
              border-radius: initial !important;
            `
              : `
              background-color: #dbeafe !important;
              border-color: #3b82f6 !important;
              border-radius: 10px !important;
            `
          }
        }
        
        .nepali-calendar-day.selected {
          background-color: #87ceeb !important;
          border-color: #5fb8d5 !important;
          border-radius: 10px !important;
        }

        .nepali-calendar-day.selected .bs-date,
        .nepali-calendar-day.selected .ad-date {
          color: white !important;
        }

        .nepali-calendar-day.selected.today {
          ${
            value
              ? `
              background-color: #87ceeb !important;
              border-color: #5fb8d5 !important;
            `
              : `
              background-color: #4169e1 !important;
              border-color: #4169e1 !important;
            `
          }
        }

        ${
          type === "default"
            ? `
          .nepali-calendar-day.holiday:not(.selected):not(.today) .bs-date,
          .nepali-calendar-day.holiday:not(.selected):not(.today) .ad-date,
          .nepali-calendar-day[data-national-holiday="true"]:not(.selected):not(.today) .bs-date,
          .nepali-calendar-day[data-national-holiday="true"]:not(.selected):not(.today) .ad-date {
            color: #ef4444 !important;
          }
        `
            : ""
        }

        .nepali-calendar-days {
          min-height: ${isMobile ? "420px" : "480px"} !important;
          grid-auto-rows: minmax(${
            isMobile ? "70px" : "80px"
          }, auto) !important;
        }
      `}</style>

      <div
        className={`nepali-calendar ${className} ${calendarWrapperClassName}`}
        style={styles.calendarWrapper}
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom:
              showToggle || showNationalHolidayToggle ? "12px" : "0",
          }}
        >
          {showToggle && (
            <div
              className={`nepali-calendar-toggle-wrapper ${toggleWrapperClassName}`}
              style={styles.toggleWrapper}
            >
              <div
                className={`nepali-calendar-toggle-switch ${toggleButtonClassName}`}
                onClick={() =>
                  handleCalendarModeChange(calendarMode === "bs" ? "ad" : "bs")
                }
                style={{
                  position: "relative",
                  width: "50px",
                  height: "24px",
                  borderRadius: "12px",
                  backgroundColor:
                    calendarMode === "bs" ? "#4169e1" : "#e5e7eb",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                  ...styles.toggleSwitch,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: calendarMode === "bs" ? "2px" : "26px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    transition: "left 0.3s",
                    ...styles.toggleSlider,
                  }}
                />
              </div>
              <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                {calendarMode === "bs" ? "BS" : "AD"}
              </span>
            </div>
          )}
          {showNationalHolidayToggle && (
            <div
              className={`nepali-calendar-toggle-wrapper ${toggleWrapperClassName}`}
              style={styles.toggleWrapper}
            >
              <div
                className={`nepali-calendar-toggle-switch ${toggleButtonClassName}`}
                onClick={() => {
                  const newValue = !showNationalHoliday;
                  setShowNationalHoliday(newValue);
                  onNationalHolidayToggleChange?.(newValue);
                }}
                style={{
                  position: "relative",
                  width: "50px",
                  height: "24px",
                  borderRadius: "12px",
                  backgroundColor: showNationalHoliday ? "#ef4444" : "#e5e7eb",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                  ...styles.toggleSwitch,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: showNationalHoliday ? "2px" : "26px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    transition: "left 0.3s",
                    ...styles.toggleSlider,
                  }}
                />
              </div>
              <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                National Holiday
              </span>
            </div>
          )}
        </div>
        <div
          className={`nepali-calendar-header ${headerClassName}`}
          style={{
            ...styles.header,
            justifyContent: showArrows ? "space-between" : "center",
          }}
        >
          {showArrows && (
            <button
              className={`nepali-calendar-nav-button ${navButtonClassName}`}
              onClick={handlePrevMonth}
              disabled={isLoading || isYearLoading || isMonthLoading}
              style={{
                ...styles.navButton,
                ...(isLoading || isYearLoading || isMonthLoading
                  ? styles.navButtonDisabled
                  : {}),
              }}
            >
              {prevArrow}
            </button>
          )}
          <div
            ref={titleRef}
            className={`nepali-calendar-title ${titleClassName}`}
            style={{
              ...styles.title,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
              cursor: "pointer",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                onClick={() => {
                  setShowMonthDropdown(!showMonthDropdown);
                  setShowYearDropdown(false);
                }}
                style={{
                  backgroundColor: showMonthDropdown
                    ? "#e8f4f8"
                    : "transparent",
                  padding: "4px 8px",
                  borderRadius: "4px",
                }}
              >
                {getDisplayMonthYear().month}
              </div>
              <span>/</span>
              <div
                onClick={() => {
                  setShowYearDropdown(!showYearDropdown);
                  setShowMonthDropdown(false);
                }}
                style={{
                  backgroundColor: showYearDropdown ? "#e8f4f8" : "transparent",
                  padding: "4px 8px",
                  borderRadius: "4px",
                }}
              >
                {getDisplayMonthYear().year}
              </div>
            </div>
            {calendarMode === "bs" && getADMonthRange() && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginLeft: "4px",
                }}
              >
                {getADMonthRange()}
              </div>
            )}
            {showMonthDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 1000,
                  width: "150px",
                  marginTop: "4px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              >
                {renderMonths()}
              </div>
            )}
            {showYearDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 1000,
                  width: "100px",
                  marginTop: "4px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              >
                {renderYears()}
              </div>
            )}
          </div>
          {showArrows && (
            <button
              className={`nepali-calendar-nav-button ${navButtonClassName}`}
              onClick={handleNextMonth}
              disabled={isLoading || isYearLoading || isMonthLoading}
              style={{
                ...styles.navButton,
                ...(isLoading || isYearLoading || isMonthLoading
                  ? styles.navButtonDisabled
                  : {}),
              }}
            >
              {nextArrow}
            </button>
          )}
        </div>
        <div
          className={`nepali-calendar-weekdays ${weekdaysClassName}`}
          style={styles.weekdays}
        >
          {weekdaysToDisplay.map((day, index) => {
            const map = [0, 1, 2, 3, 4, 5, 6];
            const set = new Set<number>(
              (Array.isArray(holidayWeekdays)
                ? holidayWeekdays
                : [holidayWeekdays]
              )
                .map((d) => String(d).trim().toLowerCase())
                .map(
                  (d) =>
                    ({
                      sunday: 0,
                      sun: 0,
                      monday: 1,
                      mon: 1,
                      tuesday: 2,
                      tue: 2,
                      wednesday: 3,
                      wed: 3,
                      thursday: 4,
                      thu: 4,
                      friday: 5,
                      fri: 5,
                      saturday: 6,
                      sat: 6,
                    }[d])
                )
                .filter((v) => v !== undefined) as number[]
            );
            const isHolidayWeekday = set.has(map[index]);

            return (
              <div
                key={index}
                className={`nepali-calendar-weekday ${weekdayClassName} ${
                  isHolidayWeekday ? "holiday" : ""
                }`}
                style={{
                  ...styles.weekday,
                  ...(isHolidayWeekday
                    ? styles.weekdayHoliday ?? styles.weekdaySaturday
                    : {}),
                }}
              >
                {day}
              </div>
            );
          })}
        </div>
        <div
          className={`nepali-calendar-days ${calendarDaysClassName}`}
          style={{ ...styles.calendarDays, position: "relative" }}
        >
          {renderCalendarDays()}
        </div>
      </div>

      {/* Event hover tooltip - Desktop only */}
      {!isMobile && hoveredEvent && (
        <div
          style={{
            position: "fixed",
            left: hoveredEvent.position.x,
            top: hoveredEvent.position.y,
            transform: "translate(-50%, calc(-100% - 8px))",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "12px",
            maxWidth: "250px",
            zIndex: 10000,
            pointerEvents: "none",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
            {hoveredEvent.event.title || ""}
          </div>
          {hoveredEvent.event.description && (
            <div>{hoveredEvent.event.description}</div>
          )}
        </div>
      )}

      {/* National holidays hover popup - Desktop only, when type === "default" */}
      {!isMobile && hoveredDay && hoveredDay.events.length > 0 && (
        <div
          style={{
            position: "fixed",
            left: hoveredDay.position.x,
            top: hoveredDay.position.y,
            transform: "translate(-50%, calc(-100% - 8px))",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            color: "white",
            padding: "12px",
            borderRadius: "6px",
            fontSize: "12px",
            maxWidth: "300px",
            zIndex: 10000,
            pointerEvents: "none",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: "8px",
              fontSize: "13px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              paddingBottom: "6px",
            }}
          >
            National Holidays
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {hoveredDay.events.map((event) => (
              <div key={event.id}>
                <div style={{ fontWeight: 500, marginBottom: "4px" }}>
                  {event.title || ""}
                </div>
                {event.description && (
                  <div
                    style={{
                      fontSize: "11px",
                      opacity: 0.9,
                      marginTop: "2px",
                    }}
                  >
                    {event.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show more modal */}
      {showMoreModal && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 9998,
            }}
            onClick={() => setShowMoreModal(null)}
          />
          <div
            style={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "16px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
              zIndex: 9999,
              minWidth: isMobile ? "90%" : "300px",
              maxWidth: isMobile ? "90%" : "400px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              Events on {showMoreModal.date}
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {showMoreModal.events.map((event) => (
                <div
                  key={event.id}
                  onClick={() => {
                    onEventClick?.(event);
                    setShowMoreModal(null);
                  }}
                  style={{
                    padding: "12px",
                    backgroundColor: event.color || "#3b82f6",
                    color: "white",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "transform 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "scale(1.02)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "scale(1)";
                    }
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                    {event.title}
                  </div>
                  {event.description && (
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.9,
                        marginBottom: "4px",
                      }}
                    >
                      {event.description}
                    </div>
                  )}
                  <div
                    style={{ fontSize: "11px", marginTop: "4px", opacity: 0.8 }}
                  >
                    {event.start === event.end
                      ? event.start
                      : `${event.start} - ${event.end}`}
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowMoreModal(null)}
              style={{
                marginTop: "12px",
                width: "100%",
                padding: "10px",
                backgroundColor: "#f3f4f6",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Close
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NepaliCalendar;
